"use client"

import { motion } from "framer-motion"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { ArrowLeft, FileText, Scale, AlertTriangle, CheckCircle } from "lucide-react"
import { Link } from "react-router-dom"
import Footer from "@/components/Common/Footer"
import ERPLeadBot from "./ERPLeadBot";

export default function TermsConditions() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <motion.div
            className="flex items-center gap-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Home</span>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <motion.div
          className="max-w-4xl mx-auto"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Header Section */}
          <motion.div className="text-center mb-12" variants={fadeIn}>
            <Badge className="mb-4 bg-blue-100 text-blue-800 hover:bg-blue-200">
              <Scale className="h-4 w-4 mr-2" />
              TERMS & CONDITIONS
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Terms & Conditions
            </h1>
            <p className="text-xl text-gray-600">
              Please read these terms carefully before using our platform
            </p>
          </motion.div>

          {/* Policy Content */}
          <motion.div variants={fadeIn}>
            <Card className="shadow-lg border-0">
              <CardContent className="p-8">
                <div className="prose prose-lg max-w-none">
                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <FileText className="h-6 w-6 mr-2 text-blue-600" />
                      Introduction
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      This document is an electronic record in terms of Information Technology Act, 2000 and rules there under as applicable and the amended provisions pertaining to electronic records in various statutes as amended by the Information Technology Act, 2000. This electronic record is generated by a computer system and does not require any physical or digital signatures.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      This document is published in accordance with the provisions of Rule 3 (1) of the Information Technology (Intermediaries guidelines) Rules, 2011 that require publishing the rules and regulations, privacy policy and Terms of Use for access or usage of domain name www.clynox.com ('Website'), including the related mobile site and mobile application (hereinafter referred to as 'Platform').
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      The Platform is owned by Clynox Solutions, a company incorporated under the Companies Act, 1956 with its registered office at 20-4 MATHRURA ROAD, NAPCO BEVAL GEAR COMPLEX, Faridabad, Harayana, 121005 (hereinafter referred to as 'Platform Owner', 'we', 'us', 'our').
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <AlertTriangle className="h-6 w-6 mr-2 text-blue-600" />
                      Important Notice
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      Your use of the Platform and services and tools are governed by the following terms and conditions ("Terms of Use") as applicable to the Platform including the applicable policies which are incorporated herein by way of reference. If You transact on the Platform, You shall be subject to the policies that are applicable to the Platform for such transaction.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      By mere use of the Platform, You shall be contracting with the Platform Owner and these terms and conditions including the policies constitute Your binding obligations, with Platform Owner. These Terms of Use relate to your use of our website, goods (as applicable) or services (as applicable) (collectively, 'Services').
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      Any terms and conditions proposed by You which are in addition to or which conflict with these Terms of Use are expressly rejected by the Platform Owner and shall be of no force or effect.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Modification of Terms</h2>
                    <p className="text-gray-700 leading-relaxed">
                      These Terms of Use can be modified at any time without assigning any reason. It is your responsibility to periodically review these Terms of Use to stay informed of updates.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-4">
                      For the purpose of these Terms of Use, wherever the context so requires 'you', 'your' or 'user' shall mean any natural or legal person who has agreed to become a user/buyer on the Platform.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Agreement to Terms</h2>
                    <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
                      <p className="text-blue-800 font-semibold">
                        ACCESSING, BROWSING OR OTHERWISE USING THE PLATFORM INDICATES YOUR AGREEMENT TO ALL THE TERMS AND CONDITIONS UNDER THESE TERMS OF USE, SO PLEASE READ THE TERMS OF USE CAREFULLY BEFORE PROCEEDING.
                      </p>
                    </div>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Terms of Use</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      The use of Platform and/or availing of our Services is subject to the following Terms of Use:
                    </p>
                    
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <p className="text-gray-700">
                          To access and use the Services, you agree to provide true, accurate and complete information to us during and after registration, and you shall be responsible for all acts done through the use of your registered account on the Platform.
                        </p>
                      </div>
                      
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <p className="text-gray-700">
                          Neither we nor any third parties provide any warranty or guarantee as to the accuracy, timeliness, performance, completeness or suitability of the information and materials offered on this website or through the Services, for any specific purpose. You acknowledge that such information and materials may contain inaccuracies or errors and we expressly exclude liability for any such inaccuracies or errors to the fullest extent permitted by law.
                        </p>
                      </div>
                      
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <p className="text-gray-700">
                          Your use of our Services and the Platform is solely and entirely at your own risk and discretion for which we shall not be liable to you in any manner. You are required to independently assess and ensure that the Services meet your requirements.
                        </p>
                      </div>
                      
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <p className="text-gray-700">
                          The contents of the Platform and the Services are proprietary to us and are licensed to us. You will not have any authority to claim any intellectual property rights, title, or interest in its contents. The contents includes and is not limited to the design, layout, look and graphics.
                        </p>
                      </div>
                      
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <p className="text-gray-700">
                          You acknowledge that unauthorized use of the Platform and/or the Services may lead to action against you as per these Terms of Use and/or applicable laws.
                        </p>
                      </div>
                      
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <p className="text-gray-700">
                          You agree to pay us the charges associated with availing the Services.
                        </p>
                      </div>
                      
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <p className="text-gray-700">
                          You agree not to use the Platform and/ or Services for any purpose that is unlawful, illegal or forbidden by these Terms, or Indian or local laws that might apply to you.
                        </p>
                      </div>
                      
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <p className="text-gray-700">
                          You agree and acknowledge that website and the Services may contain links to other third party websites. On accessing these links, you will be governed by the terms of use, privacy policy and such other policies of such third party websites. These links are provided for your convenience for provide further information.
                        </p>
                      </div>
                      
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <p className="text-gray-700">
                          You understand that upon initiating a transaction for availing the Services you are entering into a legally binding and enforceable contract with the Platform Owner for the Services.
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Indemnification</h2>
                    <p className="text-gray-700 leading-relaxed">
                      You shall indemnify and hold harmless Platform Owner, its affiliates, group companies (as applicable) and their respective officers, directors, agents, and employees, from any claim or demand, or actions including reasonable attorney's fees, made by any third party or penalty imposed due to or arising out of Your breach of this Terms of Use, privacy Policy and other Policies, or Your violation of any law, rules or regulations or the rights (including infringement of intellectual property rights) of a third party.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Force Majeure</h2>
                    <p className="text-gray-700 leading-relaxed">
                      Notwithstanding anything contained in these Terms of Use, the parties shall not be liable for any failure to perform an obligation under these Terms if performance is prevented or delayed by a force majeure event.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Governing Law</h2>
                    <p className="text-gray-700 leading-relaxed">
                      These Terms and any dispute or claim relating to it, or its enforceability, shall be governed by and construed in accordance with the laws of India.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Jurisdiction</h2>
                    <p className="text-gray-700 leading-relaxed">
                      All disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts in Faridabad, Haryana.
                    </p>
                  </motion.div>

                  <motion.div variants={fadeIn} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
                    <p className="text-gray-700 leading-relaxed">
                      All concerns or communications relating to these Terms must be communicated to us using the contact information provided on this website.
                    </p>
                  </motion.div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
      <Footer />
      <ERPLeadBot />
    </div>
  )
} 